import { ENDPOINTS } from '../utils/endpoints';
import { getAPIwithToken, postAPIwithToken, putAPIwithToken, deleteAPIwithToken } from '../utils/fetchApi';

// Categories API Interface
export interface ApiCategoryItem {
  id: number;
  name?: string;
  category_name?: string;
  status?: number | string;
}

// GET Categories API
export async function getCategories(): Promise<any> {
  return getAPIwithToken(ENDPOINTS.GET_CATEGORIES);
}

// Brands API Interfaces
export interface ApiBrandItem {
  id: number;
  name?: string;
  logoUrl?: string | null;
  status?: number | string;
  createdBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateBrandPayload {
  name: string;
  brandName?: string;
  brand_name?: string;
  sapCode?: string;
  logoUrl?: string | null;
  status?: number;
}

export interface UpdateBrandPayload {
  name?: string;
  brandName?: string;
  brand_name?: string;
  sapCode?: string;
  logoUrl?: string | null;
  status?: number;
}

// Units API Interfaces
export interface ApiUnitItem {
  id: number;
  name?: string;
  symbol?: string;
  status?: number | string;
  createdBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateUnitPayload {
  name: string;
  unitName?: string;
  unit_name?: string;
  sapCode?: string | null;
  symbol?: string | null;
  status?: number;
}

export interface UpdateUnitPayload {
  name?: string;
  unitName?: string;
  unit_name?: string;
  sapCode?: string | null;
  symbol?: string | null;
  status?: number;
}

// Sizes API Interfaces
export interface ApiSizeUnit {
  id: number;
  name?: string;
  symbol?: string;
  status?: number | string;
}

export interface ApiSizeItem {
  id: number;
  unitId?: number;
  sizeValue?: string | number;
  status?: number | string;
  createdBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
  unit?: ApiSizeUnit;
}

export interface CreateSizePayload {
  unitId?: number | string;
  unit_id?: number | string;
  sizeValue?: number | string;
  size_value?: number | string;
  sapCode?: string;
  status?: number;
}

export interface UpdateSizePayload {
  unitId?: number | string;
  unit_id?: number | string;
  sizeValue?: number | string;
  size_value?: number | string;
  sapCode?: string;
  status?: number;
}

// Packagings API Interfaces
export interface ApiPackagingProduct {
  id: number;
  name?: string;
  displayName?: string;
  materialCode?: string;
  stockQty?: number;
  status?: number;
}

export interface ApiPackagingItem {
  id: number;
  productId?: number;
  name?: string;
  packagingType?: string;
  packSize?: string | number;
  crateSize?: number | null;
  unitsPerCrate?: number | null;
  status?: number | string;
  createdBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
  product?: ApiPackagingProduct;
}

export interface CreatePackagingPayload {
  name?: string;
  packagingName?: string;
  packaging_name?: string;
  sapCode?: string;
  packagingType?: string;
  packaging_type?: string;
  crateSize?: number;
  crate_size?: number;
  unitsPerCrate?: number;
  units_per_crate?: number;
  status?: number;
}

export interface UpdatePackagingPayload {
  name?: string;
  packagingName?: string;
  packaging_name?: string;
  sapCode?: string;
  packagingType?: string;
  packaging_type?: string;
  crateSize?: number;
  crate_size?: number;
  unitsPerCrate?: number;
  units_per_crate?: number;
  status?: number;
}

// API Response Generic
export interface BaseApiResponse<T> {
  status?: string;
  success?: boolean;
  statusCode?: number;
  message?: string;
  data: T[] | { data?: T[]; items?: T[] };
}

// GET Brands API
export async function getBrands(): Promise<BaseApiResponse<ApiBrandItem>> {
  return getAPIwithToken<BaseApiResponse<ApiBrandItem>>(ENDPOINTS.GET_BRANDS);
}

// CREATE Brand API
export async function createBrand(payload: CreateBrandPayload): Promise<any> {
  return postAPIwithToken(ENDPOINTS.CREATE_BRAND, payload);
}

// UPDATE Brand API
export async function updateBrand(id: number | string, payload: UpdateBrandPayload): Promise<any> {
  return putAPIwithToken(ENDPOINTS.UPDATE_BRAND(id), payload);
}

// GET Units API
export async function getUnits(): Promise<BaseApiResponse<ApiUnitItem>> {
  return getAPIwithToken<BaseApiResponse<ApiUnitItem>>(ENDPOINTS.GET_UNITS);
}

// CREATE Unit API
export async function createUnit(payload: CreateUnitPayload): Promise<any> {
  return postAPIwithToken(ENDPOINTS.CREATE_UNIT, payload);
}

// UPDATE Unit API
export async function updateUnit(id: number | string, payload: UpdateUnitPayload): Promise<any> {
  return putAPIwithToken(ENDPOINTS.UPDATE_UNIT(id), payload);
}

// GET Sizes API
export async function getSizes(): Promise<BaseApiResponse<ApiSizeItem>> {
  return getAPIwithToken<BaseApiResponse<ApiSizeItem>>(ENDPOINTS.GET_SIZES);
}

// CREATE Size API
export async function createSize(payload: CreateSizePayload): Promise<any> {
  return postAPIwithToken(ENDPOINTS.CREATE_SIZE, payload);
}

// UPDATE Size API
export async function updateSize(id: number | string, payload: UpdateSizePayload): Promise<any> {
  return putAPIwithToken(ENDPOINTS.UPDATE_SIZE(id), payload);
}

// GET Packagings API
export async function getPackagings(): Promise<BaseApiResponse<ApiPackagingItem>> {
  return getAPIwithToken<BaseApiResponse<ApiPackagingItem>>(ENDPOINTS.GET_PACKAGINGS);
}

// CREATE Packaging API
export async function createPackaging(payload: CreatePackagingPayload): Promise<any> {
  return postAPIwithToken(ENDPOINTS.CREATE_PACKAGING, payload);
}

// UPDATE Packaging API
export async function updatePackaging(id: number | string, payload: UpdatePackagingPayload): Promise<any> {
  return putAPIwithToken(ENDPOINTS.UPDATE_PACKAGING(id), payload);
}

// Crates API Interfaces
export interface ApiCratePrimaryProduct {
  id: number;
  name: string;
  sapCode?: string;
}

export interface ApiCrateItem {
  id: number;
  name: string;
  units: number;
  createdAt?: string;
  updatedAt?: string;
  primaryProducts?: ApiCratePrimaryProduct[];
}

export interface CreateCratePayload {
  name: string;
  units: number;
}

export interface UpdateCratePayload {
  name: string;
  units: number;
}

// GET Crates API
export async function getCrates(): Promise<BaseApiResponse<ApiCrateItem>> {
  return getAPIwithToken<BaseApiResponse<ApiCrateItem>>(ENDPOINTS.GET_CRATES);
}

// GET Crate By ID API
export async function getCrateById(id: number | string): Promise<any> {
  return getAPIwithToken(ENDPOINTS.GET_CRATE_BY_ID(id));
}

// CREATE Crate API
export async function createCrate(payload: CreateCratePayload): Promise<any> {
  return postAPIwithToken(ENDPOINTS.CREATE_CRATE, payload);
}

// UPDATE Crate API
export async function updateCrate(id: number | string, payload: UpdateCratePayload): Promise<any> {
  return putAPIwithToken(ENDPOINTS.UPDATE_CRATE(id), payload);
}

// DELETE Crate API
export async function deleteCrate(id: number | string): Promise<any> {
  return deleteAPIwithToken(ENDPOINTS.DELETE_CRATE(id));
}

